import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSearchParams, useNavigate } from "react-router-dom";
import { apiClient } from "../../lib/api/client.js";
import { useMe } from "../../hooks/useMe.js";
import { downloadCsv } from "../../lib/csv.js";
import {
  createKitSchema,
  addContenitoreSchema,
  type Kit,
  type Contenitore,
  type CreateKit,
  type AddContenitore,
  type Utenza,
  type FrazioneMerceologica,
} from "@taras/contracts";

const FRAZIONI: { value: FrazioneMerceologica; label: string; color: string; bg: string; border: string }[] = [
  { value: "ORGANICO",         label: "Organico",         color: "text-amber-800",  bg: "bg-amber-100",  border: "border-amber-400" },
  { value: "CARTA",            label: "Carta",            color: "text-blue-800",   bg: "bg-blue-100",   border: "border-blue-400" },
  { value: "PLASTICA_METALLO", label: "Plastica/Metallo", color: "text-yellow-800", bg: "bg-yellow-100", border: "border-yellow-400" },
  { value: "VETRO",            label: "Vetro",            color: "text-green-800",  bg: "bg-green-100",  border: "border-green-400" },
  { value: "SECCO_RESIDUO",    label: "Secco residuo",    color: "text-gray-700",   bg: "bg-gray-100",   border: "border-gray-400" },
];
const FRAZIONI_DEFAULT = FRAZIONI[4]!;
function frazConfig(f: FrazioneMerceologica) {
  return FRAZIONI.find((x) => x.value === f) ?? FRAZIONI_DEFAULT;
}

const STATO_LABELS: Record<string, string> = {
  ASSEGNATO: "Assegnato", CONSEGNATO: "Consegnato", RITIRATO: "Ritirato", SOSTITUITO: "Sostituito",
};
const STATO_COLORS: Record<string, string> = {
  ASSEGNATO: "bg-blue-100 text-blue-700",
  CONSEGNATO: "bg-green-100 text-green-700",
  RITIRATO: "bg-gray-100 text-gray-600",
  SOSTITUITO: "bg-yellow-100 text-yellow-700",
};

type KitWithUtenza = Kit & { utenza?: Utenza; contenitori?: Contenitore[] };

function FrazioneBadge({ contenitore }: { contenitore: Contenitore }) {
  const cfg = frazConfig(contenitore.frazione_merceologica);
  return (
    <div className={`flex flex-col gap-0.5 px-2 py-1.5 rounded-lg border ${cfg.bg} ${cfg.border}`}>
      <span className={`text-xs font-semibold ${cfg.color}`}>{cfg.label}</span>
      <span className="font-mono text-[10px] text-gray-600 leading-none">{contenitore.codice_barcode}</span>
      <span className="text-[10px] text-gray-500">{contenitore.volumetria_litri} L</span>
    </div>
  );
}

function KitCard({ kit, onAddContenitore, onEdit, onViewUtenza, canWrite }: {
  kit: KitWithUtenza;
  onAddContenitore: (id: string) => void;
  onEdit: (k: KitWithUtenza) => void;
  onViewUtenza: (u: Utenza) => void;
  canWrite: boolean;
}) {
  const contenitori = kit.contenitori ?? [];
  const utenza = kit.utenza;
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-2">
        <div className="space-y-0.5">
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm font-semibold text-gray-900">{kit.codice_kit}</span>
            <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${STATO_COLORS[kit.stato] ?? ""}`}>
              {STATO_LABELS[kit.stato] ?? kit.stato}
            </span>
          </div>
          {kit.data_consegna && (
            <p className="text-xs text-gray-400">
              Consegnato il {new Intl.DateTimeFormat("it-IT").format(new Date(kit.data_consegna))}
            </p>
          )}
        </div>
        {canWrite && (
          <div className="flex gap-1.5">
            <button onClick={() => onEdit(kit)} className="shrink-0 text-xs px-2.5 py-1 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors">
              Modifica
            </button>
            {(kit.stato === "ASSEGNATO" || kit.stato === "CONSEGNATO") && (
              <button onClick={() => onAddContenitore(kit.id)} className="shrink-0 text-xs px-2.5 py-1 rounded-lg border border-blue-300 text-blue-700 hover:bg-blue-50 transition-colors">
                + Contenitore
              </button>
            )}
          </div>
        )}
      </div>

      {utenza && (
        <button
          type="button"
          onClick={() => onViewUtenza(utenza)}
          className="w-full text-left bg-gray-50 hover:bg-blue-50 rounded-lg px-3 py-2 space-y-0.5 transition-colors group"
        >
          <div className="flex items-center gap-1.5">
            <svg viewBox="0 0 16 16" fill="currentColor" className="w-3.5 h-3.5 text-gray-400 shrink-0">
              <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0Zm4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4Z" />
            </svg>
            <span className="text-sm font-medium text-gray-800 group-hover:text-blue-700">
              {utenza.intestatario_nominativo || <span className="italic text-gray-400">—</span>}
            </span>
            <span className="text-xs text-gray-400 font-mono">({utenza.codice_tari})</span>
          </div>
          <p className="text-xs text-gray-500 pl-5">{utenza.indirizzo_via} {utenza.indirizzo_civico}</p>
        </button>
      )}

      {contenitori.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {contenitori.map((c) => <FrazioneBadge key={c.id} contenitore={c} />)}
        </div>
      ) : (
        <p className="text-xs text-gray-400 italic">Nessun contenitore assegnato</p>
      )}
    </div>
  );
}

function UtenzaSearch({ onSelect }: { onSelect: (u: Utenza) => void }) {
  const [q, setQ] = useState("");
  const { data, isFetching } = useQuery<{ items: Utenza[] }>({
    queryKey: ["utenze", "search", q],
    queryFn: () => apiClient.get(`/api/v1/utenze?q=${encodeURIComponent(q)}&limit=8`),
    enabled: q.length >= 2,
    staleTime: 10_000,
  });
  return (
    <div className="relative">
      <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
        placeholder="Cerca per CF / P.IVA, codice TARI o intestatario…" className={input} />
      {isFetching && <span className="absolute right-2 top-2 text-xs text-gray-400">…</span>}
      {data && data.items.length > 0 && q.length >= 2 && (
        <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
          {data.items.map((u) => (
            <button key={u.id} type="button" onClick={() => { onSelect(u); setQ(""); }}
              className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm border-b border-gray-100 last:border-0">
              <span className="font-medium text-gray-800">{u.intestatario_nominativo || <span className="italic text-gray-400">Senza nominativo</span>}</span>
              <p className="text-xs text-gray-400 mt-0.5 space-x-2">
                <span className="font-mono">{u.codice_tari}</span>
                {u.intestatario_cf_piva && <span className="font-mono">{u.intestatario_cf_piva}</span>}
                <span>{u.indirizzo_via} {u.indirizzo_civico}</span>
              </p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const PAGE_SIZE = 50;

export function KitPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // utenza_id può arrivare dall'URL (navigazione da UtenzePage)
  const [utenzaId] = useState(() => searchParams.get("utenza_id") ?? "");
  const [utenzaNome] = useState(() => searchParams.get("nome") ?? "");

  const [q, setQ] = useState("");
  const [stato, setStato] = useState("");
  const [cursor, setCursor] = useState<string | null>(null);
  const [cursorStack, setCursorStack] = useState<string[]>([]);

  const [showNewKit, setShowNewKit] = useState(false);
  const [addContenitoreKitId, setAddContenitoreKitId] = useState<string | null>(null);
  const [editKit, setEditKit] = useState<KitWithUtenza | null>(null);
  const { data: me } = useMe();
  const canWrite = me?.ruolo !== "VISUALIZZATORE";
  const [exporting, setExporting] = useState(false);
  const [selectedUtenza, setSelectedUtenza] = useState<Utenza | null>(null);

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const p = new URLSearchParams();
      if (q.trim()) p.set("q", q.trim());
      if (stato) p.set("stato", stato);
      if (utenzaId) p.set("utenza_id", utenzaId);
      type KitRow = {
        codice_kit: string; stato: string; data_consegna: string | null; note: string | null;
        utenza?: { codice_tari: string; intestatario_nominativo: string; indirizzo_via: string; indirizzo_civico: string; tipo: string };
        contenitori?: { codice_barcode: string; frazione_merceologica: string; volumetria_litri: number; stato: string }[];
      };
      const kits = await apiClient.get<KitRow[]>(`/api/v1/kit/export?${p}`);
      const rows = kits.flatMap((k) =>
        (k.contenitori ?? [{ codice_barcode: "", frazione_merceologica: "", volumetria_litri: 0, stato: "" }]).map((c) => ({
          codice_kit: k.codice_kit,
          stato_kit: k.stato,
          data_consegna: k.data_consegna ?? "",
          note: k.note ?? "",
          codice_tari: k.utenza?.codice_tari ?? "",
          intestatario: k.utenza?.intestatario_nominativo ?? "",
          indirizzo: k.utenza ? `${k.utenza.indirizzo_via} ${k.utenza.indirizzo_civico}` : "",
          tipo_utenza: k.utenza?.tipo ?? "",
          barcode: c.codice_barcode,
          frazione: c.frazione_merceologica,
          litri: c.volumetria_litri,
          stato_contenitore: c.stato,
        }))
      );
      downloadCsv(`kit_${new Date().toISOString().slice(0, 10)}.csv`, rows);
    } finally {
      setExporting(false);
    }
  };
  const queryClient = useQueryClient();

  // Reset pagination on filter change
  useEffect(() => { setCursor(null); setCursorStack([]); }, [q, stato]);

  const buildUrl = (cur: string | null) => {
    const p = new URLSearchParams();
    p.set("limit", String(PAGE_SIZE));
    if (q.trim()) p.set("q", q.trim());
    if (stato) p.set("stato", stato);
    if (utenzaId) p.set("utenza_id", utenzaId);
    if (cur) p.set("cursor", cur);
    return `/api/v1/kit?${p}`;
  };

  const { data, isLoading, isFetching } = useQuery<{ items: KitWithUtenza[]; next_cursor: string | null }>({
    queryKey: ["kit", { q, stato, utenzaId, cursor }],
    queryFn: () => apiClient.get(buildUrl(cursor)),
    staleTime: 30_000,
  });

  const goNext = () => {
    if (!data?.next_cursor) return;
    setCursorStack((s) => [...s, cursor ?? ""]);
    setCursor(data.next_cursor);
  };
  const goPrev = () => {
    const prev = cursorStack[cursorStack.length - 1] ?? null;
    setCursorStack((s) => s.slice(0, -1));
    setCursor(prev || null);
  };

  // ── Create kit ──
  const kitForm = useForm<CreateKit>({ resolver: zodResolver(createKitSchema), defaultValues: { stato: "ASSEGNATO" } });
  const createKit = useMutation({
    mutationFn: (body: CreateKit) => apiClient.post<Kit>("/api/v1/kit", body),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["kit"] }); kitForm.reset(); setSelectedUtenza(null); setShowNewKit(false); },
  });

  // ── Edit kit ──
  const editKitForm = useForm<{ stato: string; data_consegna?: string; note?: string }>({});
  const updateKit = useMutation({
    mutationFn: ({ id, body }: { id: string; body: { stato?: string; data_consegna?: string | null; note?: string | null } }) =>
      apiClient.patch(`/api/v1/kit/${id}`, body),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["kit"] }); setEditKit(null); },
  });
  const openEditKit = (k: KitWithUtenza) => {
    setEditKit(k);
    editKitForm.reset({ stato: k.stato, data_consegna: k.data_consegna ? new Date(k.data_consegna).toISOString().slice(0, 10) : "", note: k.note ?? "" });
  };

  // ── Add contenitore ──
  const contForm = useForm<AddContenitore>({ resolver: zodResolver(addContenitoreSchema) });
  const addContenitore = useMutation({
    mutationFn: ({ kitId, body }: { kitId: string; body: AddContenitore }) =>
      apiClient.post(`/api/v1/kit/${kitId}/contenitori`, body),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["kit"] }); contForm.reset(); setAddContenitoreKitId(null); },
  });

  const hasPrev = cursorStack.length > 0;
  const hasNext = !!data?.next_cursor;
  const pageNum = cursorStack.length + 1;

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex flex-wrap items-center gap-3">
        <h2 className="text-lg font-semibold text-gray-900 shrink-0">Kit e Contenitori</h2>

        {utenzaId && (
          <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 border border-blue-200 text-sm text-blue-700">
            <span>{utenzaNome || utenzaId}</span>
            <button onClick={() => navigate("/kit")} className="ml-1 text-blue-400 hover:text-blue-700 leading-none">&times;</button>
          </div>
        )}

        <input type="search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Cerca per nominativo, codice TARI, barcode…"
          className="flex-1 min-w-48 max-w-sm px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />

        <select value={stato} onChange={(e) => setStato(e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
          <option value="">Tutti gli stati</option>
          <option value="ASSEGNATO">Assegnato</option>
          <option value="CONSEGNATO">Consegnato</option>
          <option value="RITIRATO">Ritirato</option>
          <option value="SOSTITUITO">Sostituito</option>
        </select>

        <button onClick={handleExportCsv} disabled={exporting}
          className="ml-auto px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 shrink-0 flex items-center gap-1.5">
          <svg viewBox="0 0 16 16" fill="currentColor" className="w-3.5 h-3.5 text-gray-500">
            <path fillRule="evenodd" d="M2 13.5a.5.5 0 0 0 .5.5h11a.5.5 0 0 0 0-1h-11a.5.5 0 0 0-.5.5ZM8 1a.5.5 0 0 1 .5.5v8.793l2.146-2.147a.5.5 0 0 1 .708.708l-3 3a.5.5 0 0 1-.708 0l-3-3a.5.5 0 0 1 .708-.708L7.5 10.293V1.5A.5.5 0 0 1 8 1Z" clipRule="evenodd"/>
          </svg>
          {exporting ? "Esportazione…" : "Esporta CSV"}
        </button>

        {canWrite && (
          <button onClick={() => setShowNewKit(true)}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors shrink-0">
            Nuovo kit
          </button>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        {isLoading ? (
          <p className="text-sm text-gray-500">Caricamento…</p>
        ) : data?.items.length === 0 ? (
          <p className="text-sm text-gray-400 italic">Nessun kit trovato.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {data?.items.map((k) => (
              <KitCard key={k.id} kit={k}
                onAddContenitore={(id) => { setAddContenitoreKitId(id); contForm.reset(); }}
                onEdit={openEditKit}
                onViewUtenza={(u) => navigate(`/utenze?q=${encodeURIComponent(u.codice_tari)}`)}
                canWrite={canWrite} />
            ))}
          </div>
        )}

        {/* Paginazione */}
        {(hasPrev || hasNext) && (
          <div className="flex items-center justify-between pt-2">
            <button onClick={goPrev} disabled={!hasPrev || isFetching}
              className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
              ← Precedente
            </button>
            <span className="text-sm text-gray-500">
              {isFetching ? "Caricamento…" : `Pagina ${pageNum}`}
            </span>
            <button onClick={goNext} disabled={!hasNext || isFetching}
              className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
              Successiva →
            </button>
          </div>
        )}
      </div>

      {/* Modal: Nuovo kit */}
      {showNewKit && (
        <Modal title="Nuovo kit" onClose={() => { setShowNewKit(false); kitForm.reset(); setSelectedUtenza(null); }}>
          <form onSubmit={kitForm.handleSubmit((d) => {
            if (!selectedUtenza) { kitForm.setError("utenza_id", { message: "Seleziona un'utenza" }); return; }
            createKit.mutate({ ...d, utenza_id: selectedUtenza.id });
          })} className="space-y-3">
            <Field label="Codice kit" error={kitForm.formState.errors.codice_kit?.message}>
              <input {...kitForm.register("codice_kit")} className={input} placeholder="KIT-001" />
            </Field>
            <Field label="Utenza TARI" error={kitForm.formState.errors.utenza_id?.message}>
              {selectedUtenza ? (
                <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                  <div>
                    <span className="text-sm font-medium text-gray-800">{selectedUtenza.intestatario_nominativo}</span>
                    <p className="text-xs text-gray-400 mt-0.5">{selectedUtenza.codice_tari} — {selectedUtenza.indirizzo_via} {selectedUtenza.indirizzo_civico}</p>
                  </div>
                  <button type="button" onClick={() => setSelectedUtenza(null)} className="text-gray-400 hover:text-gray-600 text-lg">&times;</button>
                </div>
              ) : (
                <UtenzaSearch onSelect={(u) => { setSelectedUtenza(u); kitForm.setValue("utenza_id", u.id); }} />
              )}
            </Field>
            <Field label="Stato" error={kitForm.formState.errors.stato?.message}>
              <select {...kitForm.register("stato")} className={input}>
                <option value="ASSEGNATO">Assegnato</option>
                <option value="CONSEGNATO">Consegnato</option>
              </select>
            </Field>
            <Field label="Data consegna"><input type="date" {...kitForm.register("data_consegna")} className={input} /></Field>
            <Field label="Note"><textarea {...kitForm.register("note")} rows={2} className={input} placeholder="opzionale" /></Field>
            {createKit.isError && <p className="text-xs text-red-600">Errore durante la creazione.</p>}
            <ModalActions onCancel={() => { setShowNewKit(false); kitForm.reset(); setSelectedUtenza(null); }} loading={createKit.isPending} label="Crea kit" />
          </form>
        </Modal>
      )}

      {/* Modal: Modifica kit */}
      {editKit && (
        <Modal title="Modifica kit" onClose={() => setEditKit(null)}>
          <form onSubmit={editKitForm.handleSubmit((d) => updateKit.mutate({
            id: editKit.id,
            body: { stato: d.stato, data_consegna: d.data_consegna || null, note: d.note || null },
          }))} className="space-y-3">
            <Field label="Stato">
              <select {...editKitForm.register("stato")} className={input}>
                <option value="ASSEGNATO">Assegnato</option>
                <option value="CONSEGNATO">Consegnato</option>
                <option value="RITIRATO">Ritirato</option>
                <option value="SOSTITUITO">Sostituito</option>
              </select>
            </Field>
            <Field label="Data consegna"><input type="date" {...editKitForm.register("data_consegna")} className={input} /></Field>
            <Field label="Note"><textarea {...editKitForm.register("note")} rows={2} className={input} /></Field>
            {updateKit.isError && <p className="text-xs text-red-600">Errore durante il salvataggio.</p>}
            <ModalActions onCancel={() => setEditKit(null)} loading={updateKit.isPending} label="Salva" />
          </form>
        </Modal>
      )}

      {/* Modal: Aggiungi contenitore */}
      {addContenitoreKitId && (
        <Modal title="Aggiungi contenitore" onClose={() => { setAddContenitoreKitId(null); contForm.reset(); }}>
          <form onSubmit={contForm.handleSubmit((body) => addContenitore.mutate({ kitId: addContenitoreKitId, body }))} className="space-y-3">
            <Field label="Frazione merceologica" error={contForm.formState.errors.frazione_merceologica?.message}>
              <div className="flex flex-wrap gap-2 pt-1">
                {FRAZIONI.map((f) => {
                  const selected = contForm.watch("frazione_merceologica") === f.value;
                  return (
                    <button key={f.value} type="button"
                      onClick={() => contForm.setValue("frazione_merceologica", f.value, { shouldValidate: true })}
                      className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${selected ? `${f.bg} ${f.border} ${f.color} ring-2 ring-offset-1 ring-blue-400` : "border-gray-200 text-gray-600 hover:border-gray-300"}`}>
                      {f.label}
                    </button>
                  );
                })}
              </div>
              <input type="hidden" {...contForm.register("frazione_merceologica")} />
            </Field>
            <Field label="Codice barcode" error={contForm.formState.errors.codice_barcode?.message}>
              <input {...contForm.register("codice_barcode")} className={input} placeholder="0040VE000001" />
            </Field>
            <Field label="Volumetria (litri)" error={contForm.formState.errors.volumetria_litri?.message}>
              <input type="number" min={1} {...contForm.register("volumetria_litri", { valueAsNumber: true })} className={input} placeholder="120" />
            </Field>
            <Field label="Data apposizione"><input type="date" {...contForm.register("data_apposizione")} className={input} /></Field>
            {addContenitore.isError && <p className="text-xs text-red-600">Errore. Barcode già in uso?</p>}
            <ModalActions onCancel={() => { setAddContenitoreKitId(null); contForm.reset(); }} loading={addContenitore.isPending} label="Aggiungi" />
          </form>
        </Modal>
      )}
    </div>
  );
}

// ─── Shared UI ────────────────────────────────────────────────────────────────

const input = "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";

function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
      {children}
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  );
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ModalActions({ onCancel, loading, label }: { onCancel: () => void; loading: boolean; label: string }) {
  return (
    <div className="flex justify-end gap-2 pt-2">
      <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">Annulla</button>
      <button type="submit" disabled={loading} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400">
        {loading ? "Salvataggio…" : label}
      </button>
    </div>
  );
}
